import { useEffect, useState, useRef, Fragment } from 'react'
import { clsx } from 'clsx'
import { useAuthStore, ROLES, isAdmin } from '../../../shared/store/authStore'
import { Card, StatCard, Badge, Button, Input, Select, RichSelect, ColorSwatch, SectionHeading, Divider, Modal, ConfirmDialog, SearchBar } from '../../../shared/components/UI'
import { QRCodeDisplay, formatRollSeq, getFinYear, printQR } from '../../../shared/lib/qrUtils'
import { vendorsApi, procurementApi, shadesApi, unitsApi } from '../../../shared/lib/api'
import { getPartyName, getRows } from '../../../shared/lib/partyType'
import { QrCode, PencilLine, Trash2, Search, AlertCircle, Plus, History, Scissors, PackagePlus, ArrowRight, User } from 'lucide-react'

const FALLBACK = '-'

const STATUS_BADGE = { available: { v: 'green', l: 'Available' }, partial: { v: 'amber', l: 'Partial' }, consumed: { v: 'gray', l: 'Consumed' } }

const emptyRoll = () => ({ shadeId: '', grossWeightKg: '', netWeightKg: '', ratePerKg: '' })

export default function ProcurementPage() {
  const { user } = useAuthStore()
  const userIsAdmin = isAdmin(user?.role)
  const isMM        = user?.role === ROLES.METALLIC_MASTER

  const [tab, setTab]     = useState('inventory')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [rolls, setRolls] = useState([])
  const [pendingPurchases, setPendingPurchases] = useState([])
  const [vendors, setVendors] = useState([])
  const [shades, setShades] = useState([])
  const [units, setUnits] = useState([])
  const [error, setError] = useState('')
  const [newRolls, setNewRolls] = useState([emptyRoll()])
  const [mmForm, setMmForm]     = useState({ partyId: '', purchaseDate: new Date().toISOString().split('T')[0], challanNo: '', billNo: '', notes: '', unitId: '' })
  const [selectedPurchase, setSelectedPurchase] = useState(null)
  const [rateInputs, setRateInputs]             = useState({}) 
  const [showQrFor, setShowQrFor]               = useState(null)
  
  // Edit/Delete state
  const [editingRoll, setEditingRoll]           = useState(null)
  const [deletingRollId, setDeletingRollId]     = useState(null)
  const [actionLoading, setActionLoading]       = useState(false)
  const [actionError, setActionError]           = useState('')

  // New Modal State
  const [showEntryModal, setShowEntryModal]     = useState(false)
  const [historyRoll, setHistoryRoll]           = useState(null)
  const [historyData, setHistoryData]           = useState(null)
  const [historyLoading, setHistoryLoading]     = useState(false)

  // Expanded UI state
  const [searchQuery, setSearchQuery]           = useState('')
  const [statusFilter, setStatusFilter]         = useState('')
  const [unitFilter, setUnitFilter]             = useState('')
  const inventoryRef = useRef(null)

  const finYear = getFinYear()
  const lastSeq = rolls.length > 0 ? Math.max(...rolls.map(r => r.seq_no || 0)) : 0
  const pendingCount = pendingPurchases.length

  const loadBaseData = async () => {
    try {
      const [vRes, sRes, uRes] = await Promise.all([
        vendorsApi.list(), 
        shadesApi.list(),
        unitsApi.list()
      ])
      setVendors(getRows(vRes))
      setShades(getRows(sRes))
      setUnits(getRows(uRes))
    } catch (err) {
      console.error('Base data error:', err)
    }
  }

  const loadTabData = async () => {
    setLoading(true)
    setError('')
    try {
      if (tab === 'inventory') {
        const res = await procurementApi.listRolls({ 
          search: searchQuery, 
          status: statusFilter,
          unitId: unitFilter 
        })
        setRolls(getRows(res))
      } else if (tab === 'pending' && userIsAdmin) {
        const res = await procurementApi.listPurchases({ isFinalised: false })
        setPendingPurchases(getRows(res))
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to load data.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadBaseData() }, [])
  useEffect(() => { loadTabData() }, [tab, statusFilter, unitFilter])

  const updateRoll = (i, k, v) => setNewRolls(r => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row))
  const addRoll    = () => setNewRolls(r => [...r, emptyRoll()])
  const removeRoll = (i) => setNewRolls(r => r.filter((_, idx) => idx !== i))

  const loadHistory = async (roll) => {
    setHistoryRoll(roll)
    setHistoryLoading(true)
    try {
      const res = await procurementApi.rollHistory(roll.id)
      setHistoryData(res.data.data)
    } catch (err) {
      console.error('History load error:', err)
    } finally {
      setHistoryLoading(false)
    }
  }
  
  const vendorOptions = [
    { label: 'Select vendor', value: '' },
    ...vendors.map((vendor) => ({ label: getPartyName(vendor), value: vendor.id }))
  ]

  const shadeOptions = shades.map(s => ({
    label: s.name,
    value: s.id,
    color: s.color,
    sub: s.code
  }))

  const submitEntry = async () => {
    if (!mmForm.partyId) return alert('Please select a vendor.')
    if (userIsAdmin && !mmForm.unitId) return alert('Please select a unit.')
    
    for (const r of newRolls) {
      if (!r.shadeId || !r.grossWeightKg || !r.netWeightKg) return alert('Please fill all roll details.')
      if (parseFloat(r.netWeightKg) > parseFloat(r.grossWeightKg)) return alert('Net weight cannot exceed gross weight.')
    }

    setSaving(true)
    try {
      await procurementApi.createPurchase({
        ...mmForm,
        rolls: newRolls
      })
      alert('Entry saved successfully!')
      setNewRolls([emptyRoll()])
      setMmForm({ ...mmForm, challanNo: '', billNo: '', notes: '' })
      setShowEntryModal(false)
      loadTabData()
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save entry.')
    } finally {
      setSaving(false)
    }
  }

  const finalisePurchase = async (pId) => {
    const pRolls = rolls.filter(r => r.purchase_id === pId)
    const updates = pRolls.map(r => ({
      id: r.id,
      rate_per_kg: rateInputs[r.id] || r.rate_per_kg || 0
    }))

    if (updates.some(u => !u.rate_per_kg)) return alert('Please enter rates for all rolls.')

    setSaving(true)
    try {
      await procurementApi.finalisePurchase(pId, { rates: updates })
      alert('Purchase finalised!')
      loadTabData()
    } catch (err) {
      alert('Failed to finalise.')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateRoll = async () => {
    if (!editingRoll) return
    setActionLoading(true)
    setActionError('')
    try {
      await procurementApi.updateRoll(editingRoll.id, {
        grossWeightKg: editingRoll.gross_weight_kg,
        netWeightKg: editingRoll.net_weight_kg,
        ratePerKg: editingRoll.rate_per_kg,
        shadeId: editingRoll.shade_id,
        partyId: editingRoll.purchase?.party_id
      })
      setEditingRoll(null)
      loadTabData()
    } catch (err) {
      setActionError(err.response?.data?.message || 'Update failed.')
    } finally {
      setActionLoading(false)
    }
  }

  const performDelete = async () => {
    if (!deletingRollId) return
    setActionLoading(true)
    try {
      await procurementApi.deleteRoll(deletingRollId)
      setDeletingRollId(null)
      loadTabData()
    } catch (err) {
      alert('Delete failed.')
    } finally {
      setActionLoading(false)
    }
  }

  const isEntryDirty = mmForm.partyId !== '' || newRolls.some(r => r.shadeId !== '' || r.grossWeightKg !== '')

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <StatCard label="Total Inventory" value={`${rolls.length} Rolls`} sub="In Stock" />
        <StatCard label="Available Stock" value={`${rolls.reduce((s, r) => s + parseFloat(r.available_weight_kg || 0), 0).toFixed(1)} KG`} sub="Total Net Weight" accent="green" />
        {userIsAdmin && <StatCard label="Pending Rates" value={pendingCount} sub="Awaiting Finalisation" accent="amber" />}
        <StatCard label="Active Shades" value={shades.length} sub="Across Categories" accent="red" />
      </div>

      {/* Tabs & Search */}
      <div className="flex flex-col gap-4">
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setTab('inventory')}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${tab === 'inventory' ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            Inventory View
          </button>
          {userIsAdmin && (
            <button onClick={() => setTab('pending')}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${tab === 'pending' ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              Pending Rates ({pendingCount})
            </button>
          )}
        </div>

        <SearchBar value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by roll code, batch, or vendor...">
          <Button onClick={() => setShowEntryModal(true)}>+ Add Entry</Button>
        </SearchBar>
      </div>

      {/* Inventory Table */}
      {tab === 'inventory' && (
        <Card bodyClass="!p-0 overflow-hidden ring-1 ring-gray-100/50">
          <table className="data-table">
            <thead>
              <tr className="bg-gray-50/50">
                <th>Roll Code</th>
                <th>Batch</th>
                <th>Shade</th>
                <th>Vendor</th>
                <th>Gross Wt</th>
                <th>Net Wt</th>
                <th>Rate</th>
                <th>Effective</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center py-20 text-gray-400">Loading inventory...</td></tr>
              ) : rolls.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-20 text-gray-400">No matching rolls found</td></tr>
              ) : (
                rolls.map(r => {
                  const s = STATUS_BADGE[r.status] || STATUS_BADGE.available
                  return (
                    <tr key={r.id} className="group hover:bg-gray-50/50 transition-colors">
                      <td className="font-mono text-xs font-black text-brand-600">{r.roll_code}</td>
                      <td>
                        <div className="flex flex-col">
                          <span className="text-xs font-black text-gray-800 leading-none">{r.batch_number || '—'}</span>
                          {r.purchase?.challan_no && (
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter mt-0.5">
                              Challan: {r.purchase.challan_no}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <ColorSwatch color={r.shade?.color} size="w-4 h-4" />
                          <span className="text-sm font-bold text-gray-700">{r.shade?.name}</span>
                        </div>
                      </td>
                      <td className="text-sm text-gray-500 font-semibold">{r.purchase?.party?.name || FALLBACK}</td>
                      <td>
                        <span className="text-sm font-black text-gray-800">{r.gross_weight_kg} <span className="text-[10px] text-gray-400 font-bold uppercase">KG</span></span>
                      </td>
                      <td>
                        <span className="text-sm font-black text-brand-600">{r.net_weight_kg} <span className="text-[10px] text-brand-400 font-bold uppercase">KG</span></span>
                      </td>
                      <td>
                        {r.rate_per_kg ? (
                          <span className="text-sm font-black text-teal-700">₹{r.rate_per_kg}</span>
                        ) : (
                          <Badge variant="amber" className="text-[10px]">Awaiting</Badge>
                        )}
                      </td>
                      <td>
                        {r.rate_per_kg && r.net_weight_kg > 0 ? (
                          <span className="text-sm font-black text-brand-700">
                            ₹{(r.is_cut_roll || r.status === 'partial') 
                              ? parseFloat(r.rate_per_kg).toFixed(2)
                              : ((r.gross_weight_kg * r.rate_per_kg) / r.net_weight_kg).toFixed(2)
                            }
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td><Badge variant={s.v}>{s.l}</Badge></td>
                      <td className="pr-4">
                        <div className="flex justify-start gap-1">
                          <button onClick={() => setEditingRoll(r)} className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-all" title="Edit Roll"><PencilLine size={16} /></button>
                          <button onClick={() => loadHistory(r)} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all" title="View History"><History size={16} /></button>
                          <button onClick={() => setShowQrFor(r.id)} className="p-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-xl transition-all" title="View QR"><QrCode size={16} /></button>
                          {userIsAdmin && <button onClick={() => setDeletingRollId(r.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all" title="Delete"><Trash2 size={16} /></button>}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </Card>
      )}

      {/* Pending Rates View */}
      {tab === 'pending' && (
        <div className="space-y-6">
          {pendingPurchases.map(p => (
            <Card key={p.id} title={`PO: ${p.challan_no || p.id} — ${p.party?.name}`} 
              action={<Button onClick={() => finalisePurchase(p.id)} disabled={saving} size="sm">Finalise Purchase</Button>}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-400 text-[10px] uppercase tracking-widest border-b">
                      <th className="px-4 py-2 text-left">Roll Code</th>
                      <th className="px-4 py-2 text-left">Shade</th>
                      <th className="px-4 py-2 text-right">Net Weight</th>
                      <th className="px-4 py-2 text-right w-40">Rate (₹/KG)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rolls.filter(r => r.purchase_id === p.id).map(r => (
                      <tr key={r.id} className="border-b last:border-0">
                        <td className="px-4 py-3 font-mono text-xs">{r.roll_code}</td>
                        <td className="px-4 py-3">{r.shade?.name}</td>
                        <td className="px-4 py-3 text-right font-bold">{r.net_weight_kg} KG</td>
                        <td className="px-4 py-3 text-right">
                          <input type="number" step="0.01" className="w-full h-9 border rounded-lg px-2 text-right font-bold text-brand-700 outline-none focus:ring-2 focus:ring-brand-500/20"
                            placeholder="0.00" value={rateInputs[r.id] || ''} onChange={(e) => setRateInputs(prev => ({ ...prev, [r.id]: e.target.value }))} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* New Entry Modal */}
      <Modal
        isOpen={showEntryModal}
        onClose={() => setShowEntryModal(false)}
        title={isMM ? 'Unloading Entry (Metallic Master)' : 'New Purchase Entry'}
        size="xl"
        confirmClose={isEntryDirty}
      >
        <div className="space-y-6">
          {isMM && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-xs text-amber-700 flex items-start gap-3">
              <div className="mt-0.5">⚠️</div>
              <p className="leading-relaxed">Entering roll details during unloading. Final purchase rates will be confirmed by management after QC.</p>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <RichSelect
              label="Vendor / Party"
              value={mmForm.partyId}
              onChange={(val) => setMmForm((p) => ({ ...p, partyId: val }))}
              options={vendorOptions}
              className="!h-11 rounded-xl shadow-sm"
              placeholder="Select vendor..."
            />
            {userIsAdmin && (
              <RichSelect
                label="Target Unit"
                value={mmForm.unitId}
                onChange={(val) => setMmForm((p) => ({ ...p, unitId: val }))}
                options={[
                  { label: 'Select Unit', value: '' },
                  ...units.filter(u => u.status === 'active').map(u => ({ label: u.name, value: u.id }))
                ]}
                className="!h-11 rounded-xl shadow-sm"
                placeholder="Select unit..."
              />
            )}
            <Input 
              label="Purchase Date" 
              type="date" 
              value={mmForm.purchaseDate} 
              onChange={(e) => setMmForm(p => ({ ...p, purchaseDate: e.target.value }))}
              className="!h-11 rounded-xl shadow-sm"
            />
            <Input 
              label="Challan / Bill No." 
              placeholder="e.g. CH-2024-001" 
              value={mmForm.challanNo} 
              onChange={(e) => setMmForm(p => ({ ...p, challanNo: e.target.value }))}
              className="!h-11 rounded-xl shadow-sm"
            />
          </div>

          <div className="overflow-x-auto border border-gray-100 rounded-2xl">
            <table className="w-full text-left">
              <thead className="bg-gray-50/50">
                <tr className="border-b border-gray-100">
                  <th className="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest w-16">Seq</th>
                  <th className="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Shade / Color Selection</th>
                  <th className="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest w-32">Gross (KG)</th>
                  <th className="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest w-32">Net (KG)</th>
                  {userIsAdmin && <th className="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest w-32">Rate (₹)</th>}
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {newRolls.map((r, i) => (
                  <tr key={i} className="group hover:bg-gray-50/30 transition-colors">
                    <td className="px-4 py-4">
                      <span className="text-[10px] font-bold text-gray-300 font-mono">#{i + 1}</span>
                    </td>
                    <td className="px-4 py-4 min-w-[240px]">
                      <RichSelect 
                        options={shadeOptions} 
                        value={r.shadeId} 
                        onChange={(val) => updateRoll(i, 'shadeId', val)}
                        placeholder="Select shade..."
                        renderItem={(opt) => (
                          <div className="flex items-center gap-3">
                            <ColorSwatch color={opt.color} />
                            <span className="text-sm font-semibold text-gray-700">{opt.label}</span>
                          </div>
                        )}
                        className="!h-11 rounded-xl"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <Input type="number" placeholder="0.0" value={r.grossWeightKg} onChange={(e) => updateRoll(i, 'grossWeightKg', e.target.value)} className="!h-11 rounded-xl text-center" />
                    </td>
                    <td className="px-4 py-4">
                      <Input type="number" placeholder="0.0" value={r.netWeightKg} onChange={(e) => updateRoll(i, 'netWeightKg', e.target.value)} className="!h-11 rounded-xl text-center" />
                    </td>
                    {userIsAdmin && (
                      <td className="px-4 py-4">
                        <Input type="number" placeholder="₹" value={r.ratePerKg} onChange={(e) => updateRoll(i, 'ratePerKg', e.target.value)} className="!h-11 rounded-xl text-teal-700 text-center" />
                      </td>
                    )}
                    <td className="px-4 py-4">
                      {newRolls.length > 1 && (
                        <button onClick={() => removeRoll(i)} className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center bg-gray-50/50 p-4 rounded-2xl border border-gray-100 border-dashed">
            <Button variant="secondary" onClick={addRoll} size="sm" className="bg-white border-brand-200 text-brand-600 hover:bg-brand-50">
              + Add More Rolls
            </Button>
            <div className="text-right">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Weight</p>
              <p className="text-base font-black text-brand-700">
                {newRolls.reduce((sum, r) => sum + (parseFloat(r.netWeightKg) || 0), 0).toFixed(2)} KG
              </p>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button onClick={submitEntry} disabled={saving} className="flex-1 py-4 rounded-2xl font-black shadow-lg shadow-brand-100">
              {saving ? 'Saving...' : 'Submit Entry'}
            </Button>
            <Button variant="secondary" onClick={() => setShowEntryModal(false)} className="flex-1 py-4 rounded-2xl border-gray-100 text-gray-500">Cancel</Button>
          </div>
        </div>
      </Modal>

      {/* Edit Roll Modal */}
      <Modal 
        isOpen={!!editingRoll} 
        onClose={() => { setEditingRoll(null); setActionError(''); }} 
        title={`Edit Roll — ${editingRoll?.roll_code}`}
        size="md"
        confirmClose={!!editingRoll}
      >
        <div className="space-y-6">
          {actionError && <div className="p-3 bg-red-50 text-red-600 text-xs rounded-lg border border-red-100 animate-in fade-in slide-in-from-top-2">{actionError}</div>}
          
          <div className="bg-brand-50/50 p-4 rounded-2xl border border-brand-100 border-dashed flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-brand-600 shadow-sm flex-shrink-0">
              <AlertCircle size={16} />
            </div>
            <p className="text-[11px] text-brand-800 font-medium leading-relaxed pt-1">
              <span className="font-bold underline">Batch Update:</span> Changing the vendor will update the entire purchase batch (Challan: <span className="font-mono">{editingRoll?.purchase?.challan_no || 'Entry'}</span>) to maintain integrity.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <RichSelect
              label="Vendor"
              value={editingRoll?.purchase?.party_id}
              onChange={(val) => {
                const updated = { ...editingRoll }
                updated.purchase.party_id = val
                setEditingRoll(updated)
              }}
              options={vendors.map(v => ({ label: v.name, value: v.id }))}
              className="rounded-xl"
            />
            <RichSelect
              label="Shade"
              value={editingRoll?.shade_id}
              onChange={(val) => setEditingRoll(p => ({ ...p, shade_id: val }))}
              options={shadeOptions}
              renderItem={(opt) => (
                <div className="flex items-center gap-3">
                  <ColorSwatch color={opt.color} size="w-4 h-4" />
                  <span className="text-sm font-bold text-gray-700">{opt.label}</span>
                </div>
              )}
              className="rounded-xl"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Gross Wt (KG)"
              value={editingRoll?.gross_weight_kg}
              onChange={(e) => setEditingRoll(p => ({ ...p, gross_weight_kg: parseFloat(e.target.value) || 0 }))}
              type="number"
              className="rounded-xl"
            />
            <Input
              label="Net Wt (KG)"
              value={editingRoll?.net_weight_kg}
              onChange={(e) => setEditingRoll(p => ({ ...p, net_weight_kg: parseFloat(e.target.value) || 0 }))}
              type="number"
              className="rounded-xl"
            />
            <Input
              label="Rate (₹/KG)"
              value={editingRoll?.rate_per_kg}
              onChange={(e) => setEditingRoll(p => ({ ...p, rate_per_kg: parseFloat(e.target.value) || 0 }))}
              type="number"
              className="rounded-xl"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-brand-400 animate-pulse" />
              <span className="text-[11px] text-gray-400 italic">Available weight will be recalculated automatically.</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-3 py-1 rounded-full border border-gray-100 shadow-sm">
              <span className="text-[10px] font-bold text-gray-400 uppercase">Consumed</span>
              <span className="text-xs font-black text-gray-600">{editingRoll?.consumed_weight_kg || 0} KG</span>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button onClick={handleUpdateRoll} disabled={actionLoading} className="flex-1 h-12 rounded-xl premium-shadow active-scale">
              {actionLoading ? 'Saving...' : 'Update Inventory'}
            </Button>
            <Button variant="secondary" onClick={() => setEditingRoll(null)} className="flex-1 h-12 rounded-xl border-gray-100 text-gray-500 font-semibold">Cancel</Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deletingRollId}
        onClose={() => setDeletingRollId(null)}
        onConfirm={() => performDelete(deletingRollId)}
        isLoading={actionLoading}
        title="Delete Roll?"
        message="This will remove the roll from inventory. This action cannot be undone."
        confirmText="Yes, Delete"
        variant="danger"
        error={actionError}
      />

      {/* Roll History Modal */}
      <Modal
        isOpen={!!historyRoll}
        onClose={() => { setHistoryRoll(null); setHistoryData(null); }}
        title={`Roll History: ${historyRoll?.roll_code}`}
        size="md"
      >
        {historyLoading ? (
          <div className="py-20 text-center text-gray-400">Loading history...</div>
        ) : historyData ? (
          <div className="space-y-6">
            {/* Summary Card */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-indigo-600">
                  <PackagePlus size={24} />
                </div>
                <div>
                  <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider">Current Status</p>
                  <p className="text-sm font-black text-gray-900">{historyData.roll.status.toUpperCase()} — {historyData.roll.availableWeight.toFixed(2)} KG Available</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold text-gray-400 uppercase leading-none mb-1">Effective Rate</p>
                <p className="text-lg font-black text-indigo-700 leading-none">₹{historyData.roll.rate || '0.00'}</p>
              </div>
            </div>

            {/* Lineage */}
            <div className="space-y-4">
              <SectionHeading className="!mb-0">Lineage & Source</SectionHeading>
              
              {historyData.lineage.createdVia ? (
                <div className="p-4 bg-white border border-gray-100 rounded-xl space-y-3 shadow-sm">
                  <div className="flex items-center gap-2">
                    <Scissors size={14} className="text-indigo-500" />
                    <span className="text-xs font-black text-gray-800">Created via Cutting Session</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <p className="text-gray-400 font-medium">Source Parent</p>
                      <p className="font-bold text-indigo-600 font-mono tracking-tighter">{historyData.lineage.createdVia.parentCode}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 font-medium">Performed By</p>
                      <div className="flex items-center gap-1.5 font-bold text-gray-700">
                        <User size={12} className="text-gray-400" />
                        {historyData.lineage.createdVia.performedBy}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-white border border-gray-100 rounded-xl flex items-center gap-3">
                  <Badge variant="blue">Initial Procurement</Badge>
                  <span className="text-xs font-medium text-gray-500">Received from <span className="font-bold text-gray-800">{historyData.roll.vendor}</span> on {new Date(historyData.roll.purchaseDate).toLocaleDateString()}</span>
                </div>
              )}

              {historyData.lineage.children?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">Descendants (Cut Rolls)</p>
                  <div className="grid grid-cols-2 gap-2">
                    {historyData.lineage.children.map(child => (
                      <div key={child.id} className="p-2 bg-gray-50 border border-gray-100 rounded-lg flex justify-between items-center group">
                        <span className="text-xs font-mono font-bold text-gray-700 group-hover:text-indigo-600 transition-colors">{child.roll_code}</span>
                        <Badge variant={STATUS_BADGE[child.status]?.v || 'gray'} size="xs">{child.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Cutting Sessions */}
            {historyData.cuttingSessions?.length > 0 && (
              <div className="space-y-4">
                <SectionHeading className="!mb-0">Cutting & Wastage Log</SectionHeading>
                <div className="space-y-3">
                  {historyData.cuttingSessions.map(session => (
                    <div key={session.id} className="p-4 bg-gray-50 border border-gray-100 rounded-2xl relative overflow-hidden group hover:border-indigo-200 transition-all">
                      {session.wastage > 0 && <div className="absolute top-0 right-0 px-3 py-1 bg-red-100 text-red-700 text-[9px] font-black uppercase rounded-bl-xl">Wastage Detected</div>}
                      
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-tighter leading-none mb-1">Session #{session.logNumber}</p>
                          <p className="text-xs font-bold text-gray-800">{new Date(session.date).toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-black text-indigo-600 leading-none">{session.cutWeight.toFixed(2)} KG</p>
                          <p className="text-[10px] text-gray-400 font-bold leading-none mt-1">{session.count} Rolls Cut</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 py-2 border-t border-gray-200/50">
                        <div className="text-center">
                          <p className="text-[9px] font-bold text-gray-400 uppercase">Input</p>
                          <p className="text-xs font-black text-gray-700">{session.weightBefore.toFixed(2)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[9px] font-bold text-gray-400 uppercase">Wastage</p>
                          <p className={clsx("text-xs font-black", session.wastage > 0 ? "text-red-600" : "text-gray-300")}>{session.wastage.toFixed(2)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[9px] font-bold text-gray-400 uppercase">Returned</p>
                          <p className={clsx("text-xs font-black", session.returned > 0 ? "text-indigo-600" : "text-gray-300")}>{session.returned.toFixed(2)}</p>
                        </div>
                      </div>

                      {session.wastageReason && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-100/50 rounded-lg">
                          <p className="text-[9px] font-black text-red-500 uppercase leading-none mb-1">Reason</p>
                          <p className="text-[11px] text-red-700 font-medium italic">"{session.wastageReason}"</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <Button onClick={() => { setHistoryRoll(null); setHistoryData(null); }} className="w-full h-12 rounded-xl">Close History</Button>
          </div>
        ) : (
          <div className="py-20 text-center text-gray-400">No history found for this roll.</div>
        )}
      </Modal>

      {/* QR Modal */}
      {showQrFor && (
        <Modal isOpen={!!showQrFor} onClose={() => setShowQrFor(null)} title="Inventory QR Label" size="sm">
          <div className="flex flex-col items-center gap-6 py-4">
            {rolls.find(r => r.id === showQrFor) && (
              <>
                <QRCodeDisplay 
                  value={rolls.find(r => r.id === showQrFor).roll_code} 
                  label={rolls.find(r => r.id === showQrFor).roll_code}
                  sublabel={`${rolls.find(r => r.id === showQrFor).shade?.name} • ${rolls.find(r => r.id === showQrFor).net_weight_kg} KG`}
                  size={180}
                />
                <Button className="w-full rounded-2xl py-3 font-black" onClick={() => printQR(rolls.find(r => r.id === showQrFor).roll_code, rolls.find(r => r.id === showQrFor).roll_code)}>
                  Print QR Label
                </Button>
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}
